import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Trophy, Star , ArrowLeft } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import Navigation from '@/components/Navigation';
import ParticipantProfileModal from '@/components/ParticipantProfileModal';
import { useTheme } from '@/contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

const TeamPage = ({ user }) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    fetchTeam();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams/my`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeam(data);
      }
    } catch (error) {
      console.error('Error fetching team:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-2xl font-heading font-bold text-primary">Loading team...</div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen flex items-center justify-center pb-24 bg-background">
        <div className="text-center">
          <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No team assigned yet</p>
          <p className="text-sm text-muted-foreground mt-1">Your team will be assigned by your program admin</p>
        </div>
        <Navigation role="youth" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div className="p-6 md:p-8 text-white rounded-b-3xl shadow-lg mb-6" style={{ background: colors.header_gradient || colors.header }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
            <div className="w-16 h-16 bg-white/20 rounded-3xl flex items-center justify-center">
              <Users className="w-8 h-8" />
            </div>
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">{team.name}</h1>
              <p className="text-lg" style={{ color: "rgba(255,255,255,0.9)" }}>{team.member_details?.length} amazing members</p>
            </div>
          </div>

          {/* Team Stats */}
          <motion.div
            data-testid="team-stats-card"
            whileHover={{ scale: 1.02 }}
            className="bg-white/20 backdrop-blur-lg rounded-3xl p-6 border border-white/30 mt-6"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm opacity-90">Total Team Points</p>
              <Trophy className="w-6 h-6 text-accent" />
            </div>
            <p className="font-heading text-5xl font-bold tracking-tight">{team.total_points}</p>
            <div className="mt-4">
              <Progress value={65} className="h-2 bg-white/30" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Team Members */}
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2">
          <Star className="w-6 h-6 text-primary" />
          Team Members
        </h2>

        <div className="space-y-3">
          {team.member_details?.map((member, index) => {
            const isCurrentUser = member.id === user.id;
            return (
              <motion.div
                key={member.id}
                data-testid={`team-member-${index}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => !isCurrentUser && setSelectedMember(member.id)}
                className={`bg-white rounded-2xl p-4 shadow-card cursor-pointer hover:shadow-md transition-all ${
                  isCurrentUser ? 'ring-2 ring-primary' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  <Avatar className="w-14 h-14">
                    <AvatarFallback className="bg-primary/10 text-primary font-bold text-lg">
                      {member.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold text-foreground text-lg">
                      {member.name} {isCurrentUser && '(You)'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-heading font-bold text-primary">{member.total_points}</p>
                    <p className="text-xs text-muted-foreground">points</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <ParticipantProfileModal
        participantId={selectedMember}
        isOpen={!!selectedMember}
        onClose={() => setSelectedMember(null)}
      />

      <Navigation role="youth" />
    </div>
  );
};

export default TeamPage;
